import { IUnplannedRequest } from "online-services-types";
import React from "react";
import { IResponsiveTableColumn } from "src/components/ResponsiveTable/interfaces";
import { Button, ButtonStyle } from "src/design-system/Button";
import { Container } from "src/design-system/Container";
import { translateString } from "src/util/localization";
import { getRoutes, redirectBrowser } from "src/routes";
import styled from "styled-components";
import {
  UnplannedRequestFilters,
  UnplannedRequestQueryParams,
  UnplannedRequestSortOption,
} from "./unplannedRequest.types";
import { formatDate } from "src/util/formatters";

export const UNPLANNED_REQUEST_TYPES = ["Spare Part Replenishment"];
export const UNPLANNED_REQUEST_STATUSES = [
  "Draft",
  "Submitted",
  "Under Review",
  "Information Needed",
  "Waiting for PO",
  "Fully Approved",
  "Partially Approved",
  "Not Approved",
  "Under Discussion",
];

export const EVENT_DESCRIPTION_MAX_LENGTH = 4000;
export const DEFAULT_QUERY_LIMIT = 10;
export const DEFAULT_QUERY_OFFSET = 0;
export const DEFAULT_SORT_BY = "createdDate";
export const DEFAULT_SORT_ORDER = "DESC";

export function getQueryParamsFromFilters(filters: UnplannedRequestFilters): UnplannedRequestQueryParams {
  const params: UnplannedRequestQueryParams = {};
  if (filters.id) params.id = filters.id;
  if (filters.account && filters.account.length > 0) params.account = filters.account.join(",");
  if (filters.installation && filters.installation.length > 0) params.installation = filters.installation.join(",");
  if (filters.equipment && filters.equipment.length > 0) params.equipment = filters.equipment.join(",");
  if (filters.requestType && filters.requestType.length > 0) params.requestType = filters.requestType.join(",");
  if (filters.status && filters.status.length > 0) params.status = filters.status.join(",");
  if (filters.customerPONumber) params.customerPONumber = filters.customerPONumber;
  if (filters.submittedDateFrom) params.submittedDateFrom = filters.submittedDateFrom;
  if (filters.submittedDateTo) params.submittedDateTo = filters.submittedDateTo;
  if (filters.limit && filters.limit !== DEFAULT_QUERY_LIMIT) params.limit = filters.limit.toString();
  if (filters.offset && filters.offset !== DEFAULT_QUERY_OFFSET) params.offset = filters.offset.toString();
  if (filters.sortBy && filters.sortBy !== DEFAULT_SORT_BY) params.sortBy = filters.sortBy;
  if (filters.sortOrder && filters.sortOrder !== DEFAULT_SORT_ORDER) params.sortOrder = filters.sortOrder;
  return params;
}

export const getSortOptions = (): UnplannedRequestSortOption[] => [
  {
    label: translateString("sorting.newestFirst"),
    sortBy: "submittedDate",
    sortOrder: "DESC",
  },
  {
    label: translateString("sorting.oldestFirst"),
    sortBy: "submittedDate",
    sortOrder: "ASC",
  },
  {
    label: translateString("sorting.installationNameAZ"),
    sortBy: "installationName",
    sortOrder: "ASC",
  },
  {
    label: translateString("sorting.subjectAZ"),
    sortBy: "subject",
    sortOrder: "ASC",
  },
];

const DraftButtonsWrapper = styled.div`
  display: inline-flex;
  gap: 12px;
  margin: 8px 0 4px;
`;

export const getColumns = (onDelete?: (id: string) => void): IResponsiveTableColumn<IUnplannedRequest>[] => [
  {
    priority: 11,
    plannedWidth: 150,
    key: "subject",
    label: translateString("request.subject"),
  },
  {
    priority: 10,
    plannedWidth: 150,
    key: "installationName",
    label: translateString("installation"),
  },
  {
    priority: 9,
    plannedWidth: 150,
    key: "customerPONumber",
    label: translateString("contracts.unplannedRequests.poNumber"),
  },
  {
    priority: 8,
    plannedWidth: 150,
    key: "salesOrders",
    label: translateString("contracts.unplannedRequests.salesOrder"),
    valueRenderer: ({ row: { salesOrders } }) => (salesOrders ? salesOrders.join(", ") : ""),
  },
  {
    priority: 7,
    plannedWidth: 150,
    key: "type",
    label: translateString("contracts.unplannedRequests.requestType"),
  },
  {
    priority: 6,
    plannedWidth: 150,
    key: "status",
    label: translateString("status"),
  },
  {
    priority: 5,
    plannedWidth: 150,
    key: "name",
    label: translateString("contracts.unplannedRequests.requestNumber"),
  },
  {
    priority: 4,
    plannedWidth: 150,
    key: "submittedDate",
    label: translateString("contracts.unplannedRequests.submittedDate"),
    valueRenderer: ({ row: { submittedDate } }) => (submittedDate ? formatDate(submittedDate) : ""),
  },
  {
    priority: 3,
    plannedWidth: 150,
    key: "lastModifiedDate",
    label: translateString("request.lastModified"),
    valueRenderer: ({ row: { lastModifiedDate } }) => (lastModifiedDate ? formatDate(lastModifiedDate) : ""),
  },
  {
    priority: 1,
    plannedWidth: 300,
    key: "draftButtons",
    label: "",
    alwaysHidden: true,
    renderer: ({ row }) => {
      if (row.status === "Draft") {
        return (
          <Container $padding={[1, 0]}>
            <DraftButtonsWrapper>
              <Button
                onClick={() =>
                  redirectBrowser(getRoutes().CreateUnplannedRequest, {
                    phase: 1,
                    requestType: row.type,
                    draftId: row.id,
                  })
                }
              >
                {translateString("draft.continueEditing")}
              </Button>
              {onDelete && (
                <Button buttonStyle={ButtonStyle.Secondary} onClick={() => onDelete(row.id)}>
                  {translateString("draft.delete")}
                </Button>
              )}
            </DraftButtonsWrapper>
          </Container>
        );
      }
      return (
        <Container $padding={[1, 0]}>
          <Button
            onClick={() =>
              redirectBrowser(getRoutes().CreateUnplannedRequest, {
                phase: 1,
                requestType: row.type,
                draftId: row.id,
              })
            }
          >
            {translateString("button.open")}
          </Button>
        </Container>
      );
    },
  },
];
